import React, { useState } from 'react';
import AppButton from '@mysimpledb/shared/components/UI/Button/Button';
import axios from '@mysimpledb/shared/config/axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleCheck } from '@fortawesome/free-solid-svg-icons';
import './CreateAccount.css';

function getSmsStatusMessage(status) {
  switch (status) {
    case 'enabled':
      return {
        title: 'SMS notifications enabled',
        body: 'Your mobile number has been saved and you have opted in to receive optional MySimpleDB SMS notifications. You can update your SMS preferences at any time from Account Settings.',
      };

    case 'missing_phone':
      return {
        title: 'SMS notifications not enabled',
        body: 'You selected SMS notifications but did not provide a mobile phone number. You can enable SMS notifications later from Account Settings.',
      };

    case 'invalid_phone':
      return {
        title: 'SMS notifications not enabled',
        body: 'The mobile phone number you entered is invalid and could not be used for SMS notifications. You can update your phone number and enable SMS notifications later from Account Settings.',
      };

    case 'phone_already_used':
      return {
        title: 'Phone number already in use',
        body: 'The mobile phone number you entered is already associated with another MySimpleDB account. Please use a different mobile phone number or disable SMS notifications.',
      };

    case 'not_enabled_no_consent':
      return {
        title: 'SMS notifications not enabled',
        body: 'A mobile phone number was provided, but SMS consent was not selected. You can enable SMS notifications at any time from Account Settings.',
      };

    case 'already_enabled':
      return {
        title: 'SMS notifications are already enabled',
        body: 'SMS notifications are already enabled for your account using your current mobile phone number. You can update your mobile phone number or SMS preferences at any time from Account Settings.',
      };

    case 'phone_in_use':
      return {
        title: 'SMS notifications not enabled',
        body: 'This mobile phone number is already registered to receive MySimpleDB SMS notifications for another account. Your MySimpleDB account was created successfully, but SMS notifications were not enabled for this account.\n\nYou can continue using MySimpleDB normally. If you would like to enable SMS notifications later, you can use a different mobile phone number from Account Settings.',
      };

    default:
      return {
        title: 'SMS notifications were not enabled',
        body: 'You chose not to enable optional SMS notifications for your account.\n\nYou can continue using MySimpleDB normally. If you decide to enable optional SMS notifications in the future, you can enable them at any time from Account Settings.',
      };
  }
}

export default function SuccessAccount({
  email,
  devVerificationLink = null,
  onGoToLogin,
  resendEndpoint = '/auth/resend-verification',
  smsStatus,
}) {
  const smsEnabled = smsStatus === 'enabled';

  const [resendStatus, setResendStatus] = useState('idle'); // idle | sending | sent | error
  const [message, setMessage] = useState('');

  const isDevMode = Boolean(devVerificationLink);

  async function handleResend() {
    setResendStatus('sending');
    setMessage('');

    try {
      await axios.post(resendEndpoint, { email });

      setResendStatus('sent');
      setMessage(
        'Verification email resent. Please check your inbox (and spam/junk).'
      );
    } catch (e) {
      console.error('Resend verification failed', e);
      setResendStatus('error');

      const msg =
        e?.response?.data?.error ||
        e?.response?.data?.message ||
        e?.message ||
        'Could not resend verification email. Please try again.';
      setMessage(msg);
    }
  }

  function handleVerifyEmail() {
    if (!devVerificationLink) return;
    window.location.href = devVerificationLink;
  }

  const smsInfo = getSmsStatusMessage(smsStatus);

  return (
    <div className="pageWithHeaderOffset">
      <div className="create-account-wrap">
        <div className="create-account-card">
          <h2 className="create-account-title">
            {isDevMode ? 'Verify Your Email' : 'Check Your Email'}
          </h2>

          <div className="muted" style={{ marginTop: 8 }}>
            {isDevMode
              ? 'Your account was created for:'
              : 'We sent a verification link to:'}
          </div>

          <div style={{ marginTop: 6, fontWeight: 700 }}>{email}</div>

          <div className="muted" style={{ marginTop: 12 }}>
            {isDevMode
              ? 'Since you are running locally, click the button below to verify your email.'
              : 'Please click the link in that email to verify your address. After verification, you can log in.'}
          </div>

          <div
            className={`sms-status-box ${
              smsEnabled
                ? 'sms-status-box--enabled'
                : 'sms-status-box--not-enabled'
            }`}
          >
            <div className="sms-status-heading">
              {smsEnabled && (
                <span className="sms-status-icon">
                  <FontAwesomeIcon icon={faCircleCheck} />
                </span>
              )}
              <strong>{smsInfo.title}</strong>
            </div>
            <p style={{ whiteSpace: 'pre-line' }}>{smsInfo.body}</p>
          </div>

          {message ? (
            <div
              className={
                resendStatus === 'error'
                  ? 'create-account-error'
                  : 'create-account-success'
              }
              style={{ marginTop: 14 }}
            >
              {message}
            </div>
          ) : null}

          <div className="actions" style={{ marginTop: 18 }}>
            {isDevMode ? (
              <AppButton onClick={handleVerifyEmail}>Verify Email</AppButton>
            ) : (
              <AppButton
                onClick={handleResend}
                disabled={resendStatus === 'sending'}
              >
                {resendStatus === 'sending'
                  ? 'Resending…'
                  : 'Resend verification email'}
              </AppButton>
            )}

            <AppButton onClick={() => onGoToLogin?.()}>Back to Login</AppButton>
          </div>

          <div className="muted" style={{ marginTop: 14 }}>
            {isDevMode
              ? 'This button uses the local verification link returned by the backend.'
              : 'Tip: If you don’t see it within a minute, check your spam/junk folder.'}
          </div>
        </div>
      </div>
    </div>
  );
}
